import { Formik } from 'formik';
import propTypes from 'prop-types';
import { useContext, useRef } from 'react';
import { object, string } from 'yup';
import { BRAND_WOWCHER } from '../../config/setup/setup';
import {
  REQUIRED_FIELD,
  SEND_AS_E_GIFT,
  YOUR_NAME,
  GIFTEE_NAME,
  GIFTEE_EMAIL,
  ENTER_VALID_EMAIL,
  YOUR_MESSAGE,
  EGIFT_DESCRITPION_ONE,
  EGIFT_DESCRITPION_TWO,
  SEND_ME_EGIFT,
  SEND_THEM_EGIFT,
  OR,
  GENERIC_FAILURE,
  SENT_TO_SELF_SUCCESS,
  SENT_TO_RECIPIENT_SUCCESS,
} from '../../config/text/text';
import ErrorTrackingService from '../../helpers/errorTrackingService';
import { GiftOptionType, sendGift } from '../../helpers/gifting';
import ThemeContext from '../../providers/ThemeProvider';
import Button from '../_generic/button/CTAButton';
import FormikInput from '../_generic/input/FormikInput';
import FormikTextarea from '../_generic/input/FormikTextarea';
import Spinner from '../_generic/spinner/Spinner';
import useToast from '../_generic/toast/UseToast';
import { DEFAULT_EVENT_KEY } from './constants';

const schema = object().shape({
  email: string().required(REQUIRED_FIELD).email(ENTER_VALID_EMAIL),
  message: string(),
  name: string().required(REQUIRED_FIELD),
  senderName: string().required(REQUIRED_FIELD),
});

const VoucherGiftForm = ({ voucher, setToggle, mutate }) => {
  const toast = useToast();
  const theme = useContext(ThemeContext);

  // handle 2 submit buttons with different flows.
  const sendToSelf = useRef(false);
  const Environment = process.env.NEXT_PUBLIC_BRAND || BRAND_WOWCHER;
  const sendForm = async (values) => {
    try {
      const giftOptionType = sendToSelf.current
        ? GiftOptionType.download
        : GiftOptionType.eVoucher;
      await sendGift({ giftOptionType, values, voucher });
      await mutate();
      setToggle(DEFAULT_EVENT_KEY);
      if (sendToSelf.current) {
        toast.addToast(
          SENT_TO_SELF_SUCCESS[process.env.NEXT_PUBLIC_BRAND],
          'toast-success',
        );
      } else {
        toast.addToast(
          SENT_TO_RECIPIENT_SUCCESS[process.env.NEXT_PUBLIC_BRAND],
          'toast-success',
        );
      }
    } catch (error) {
      toast.addToast(GENERIC_FAILURE, 'toast-error');
      ErrorTrackingService.logError(error);
    }
  };

  return (
    <>
      <h2 className="title">{SEND_AS_E_GIFT}</h2>
      <Formik
        enableReinitialize
        initialValues={{
          email: voucher.gift?.giftEmail || '',
          message: voucher.gift?.giftMessage || '',
          name: voucher.gift?.recipientName || '',
          senderName: voucher.gift?.senderName || '',
        }}
        onSubmit={sendForm}
        validateOnBlur
        validateOnChange
        validateOnMount
        validationSchema={schema}
      >
        {({
          handleChange,
          handleSubmit,
          touched,
          handleBlur,
          isSubmitting,
          isValid,
          values,
          errors,
          submitForm,
        }) => {
          return (
            <form name="send-gift-form" onSubmit={handleSubmit}>
              {isSubmitting && <Spinner display="absolute" />}
              <FormikInput
                errorText={errors.senderName}
                inputError={errors.senderName}
                inputTouched={touched.senderName}
                inputValue={values.senderName}
                name="senderName"
                onBlurFunction={handleBlur}
                onChangeFunction={handleChange}
                placeholder={`${YOUR_NAME}*`}
                required
                type="text"
              />
              <FormikInput
                errorText={errors.name}
                inputError={errors.name}
                inputTouched={touched.name}
                inputValue={values.name}
                name="name"
                onBlurFunction={handleBlur}
                onChangeFunction={handleChange}
                placeholder={`${GIFTEE_NAME}*`}
                required
                type="text"
              />
              <FormikInput
                errorText={errors.email}
                inputError={errors.email}
                inputTouched={touched.email}
                inputValue={values.email}
                name="email"
                onBlurFunction={handleBlur}
                onChangeFunction={handleChange}
                placeholder={`${GIFTEE_EMAIL}*`}
                required
                type="email"
              />
              <FormikTextarea
                errorText={errors.message}
                inputError={errors.message}
                inputTouched={touched.message}
                inputValue={values.message}
                maxLength={175}
                name="message"
                onBlurFunction={handleBlur}
                onChangeFunction={handleChange}
                placeholder={YOUR_MESSAGE}
              />
              <p className="required">*{REQUIRED_FIELD}</p>
              <div className="buttons">
                <Button
                  className="full-width"
                  disabled={!isValid || isSubmitting}
                  onClick={() => {
                    sendToSelf.current = true;
                    submitForm();
                  }}
                  primaryAction={Environment !== BRAND_WOWCHER}
                  secondaryButton={Environment === BRAND_WOWCHER}
                  type="submit"
                >
                  {SEND_ME_EGIFT}
                </Button>
                <div className="or">{OR}</div>
                <Button
                  className="full-width"
                  disabled={!isValid || isSubmitting}
                  onClick={() => {
                    sendToSelf.current = false;
                    submitForm();
                  }}
                  primaryAction={Environment !== BRAND_WOWCHER}
                  secondaryButton={Environment === BRAND_WOWCHER}
                  type="submit"
                >
                  {SEND_THEM_EGIFT}
                </Button>
              </div>
              <p>{EGIFT_DESCRITPION_ONE}</p>
              <p>{EGIFT_DESCRITPION_TWO}</p>
            </form>
          );
        }}
      </Formik>
      <style jsx>{`
        .title {
          color: ${theme.colors.primary};
          font-weight: normal;
          font-size: 1.7rem;
          margin-bottom: 15px;
        }
        .required {
          font-size: 0.8em;
          text-align: right;
          margin-bottom: 15px;
        }
        .buttons {
          display: flex;
          flex-direction: column;
          margin-bottom: 15px;
        }
        .or {
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: #fff;
          border-radius: 50%;
          height: 42px;
          width: 42px;
          flex-shrink: 0;
          margin: 10px auto;
        }
        @media (min-width: ${theme.breakpoints.lgUp}) {
          .buttons {
            flex-direction: row;
          }
          .or {
            margin: 0 20px;
          }
        }
      `}</style>
    </>
  );
};

VoucherGiftForm.propTypes = {
  mutate: propTypes.func,
  setToggle: propTypes.func.isRequired,
  voucher: propTypes.object,
};

export default VoucherGiftForm;
